import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

// Contexts
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Auth
import Login from './components/auth/Login';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AcceptInvitation from './components/auth/AcceptInvitation';

// Dashboard
import OwnerDashboard from './components/dashboard/OwnerDashboard';
import AdminDashboard from './components/dashboard/AdminDashboard';

// Layout
import Layout from './components/layout/Layout';

// Users
import Users from './components/users/Users';
import UserDetails from './components/users/UserDetails';

// Assignment
import NewAssignment from './components/assignments/NewAssignment';
import Assignments from './components/assignments/Assignments';
import MyAssignments from './components/assignments/MyAssignments';
import MyDeliveries from './components/assignments/MyDeliveries';

import SingleAssignment from './components/assignments/SingleAssignment';
import EditAssignment from './components/assignments/EditAssignment';
import ScheduleHub from './components/schedule/ScheduleHub';
import ProductionSchedule from './components/schedule/ProductionSchedule';
import EditorSchedule from './components/schedule/EditorSchedule';
import DashboardLayout from './components/dashboard/DashboardLayout';
import QuoteSchedule from './components/schedule/QuoteSchedule';
import DeliveryQuoteSchedule from './components/schedule/DeliveryQuoteSchedule';


// Companies
import CreateCompany from './components/companies/CreateCompany';
import Companies from './components/companies/Companies';
import CompanyDetails from './components/companies/CompanyDetails';

// Communication
import CommunicationHub from './components/communication/CommunicationHub';
import NotFound from './components/common/NotFound';
import DeliveryTracker from './components/tracking/DeliveryTracker';

// Root redirect
const RootRedirect = () => {
  const { userProfile, company, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-zinc-900 p-8">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Header Skeleton */}
          <div className="bg-white dark:bg-zinc-800 rounded-2xl p-8 border border-gray-200 dark:border-zinc-700">
            <div className="flex items-center gap-3 mb-4">
              <Skeleton circle width={48} height={48} />
              <div className="flex-1">
                <Skeleton width={300} height={32} className="mb-2" />
                <Skeleton width={200} height={16} />
              </div>
            </div>
          </div>

          {/* Stats Grid Skeleton */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, index) => (
              <div key={index} className="bg-white dark:bg-zinc-800 rounded-xl p-6 border border-gray-200 dark:border-zinc-700">
                <Skeleton circle width={48} height={48} className="mb-4" />
                <Skeleton width={120} height={16} className="mb-2" />
                <Skeleton width={80} height={36} />
              </div>
            ))}
          </div>

          {/* Content Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, index) => (
              <div key={index} className="bg-white dark:bg-zinc-800 rounded-xl p-6 border border-gray-200 dark:border-zinc-700">
                <Skeleton width={150} height={24} className="mb-4" />
                <Skeleton count={3} height={16} className="mb-2" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }


  console.log(userProfile, company)

  if (userProfile && company) {

    return <Navigate to={`/${company.slug}/dashboard`} replace />;
  }

  return <Navigate to="/login" replace />;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Layout>
          <Routes>

            <Route path="/login" element={<Login />} />
            <Route path="/accept-invitation/:invitationId" element={<AcceptInvitation />} />

            <Route
              path="/:companySlug/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            />

            <Route
              path="/:companySlug/users"
              element={
                <ProtectedRoute roles={["super_admin", "admin", "designer", "estimator", "driver"]}>
                  <Users />
                </ProtectedRoute>
              }
            />

            <Route
              path="/:companySlug/users/:userId"
              element={
                <ProtectedRoute roles={["app_owner", "super_admin", "admin"]}>
                  <UserDetails />
                </ProtectedRoute>
              }
            />

            {/* APP OWNER */}
            <Route
              path="/:companySlug/companies"
              element={
                <ProtectedRoute roles={["app_owner"]}>
                  <Companies />
                </ProtectedRoute>
              }
            />

            <Route
              path="/:companySlug/companies/create"
              element={
                <ProtectedRoute roles={["app_owner"]}>
                  <CreateCompany />
                </ProtectedRoute>
              }
            />

            <Route
              path="/:companySlug/companies/:targetCompanySlug"
              element={
                <ProtectedRoute roles={["app_owner"]}>
                  <CompanyDetails />
                </ProtectedRoute>
              }
            />

            <Route
              path="/:companySlug/communicate"
              element={
                <ProtectedRoute roles={["app_owner"]}>
                  <CommunicationHub />
                </ProtectedRoute>
              }
            />

            {/* COMPANY ROUTES */}
            <Route
              path="/:companySlug/assignment/new"
              element={
                <ProtectedRoute roles={["super_admin", "admin"]}>
                  <NewAssignment />
                </ProtectedRoute>
              }
            />

            <Route
              path="/:companySlug/assignment/list"
              element={
                <ProtectedRoute roles={["super_admin", "admin", "designer", "estimator"]}>
                  <Assignments />
                </ProtectedRoute>
              }
            />

            <Route
              path="/:companySlug/assignment/my-assignments"
              element={
                <ProtectedRoute>
                  <MyAssignments />
                </ProtectedRoute>
              }
            />

            <Route
              path="/:companySlug/my-deliveries"
              element={
                <ProtectedRoute>
                  <MyDeliveries />
                </ProtectedRoute>
              }
            />

            <Route
              path="/:companySlug/assignment/:jobId"
              element={
                <ProtectedRoute>
                  <SingleAssignment />
                </ProtectedRoute>
              }
            />

            <Route
              path="/:companySlug/assignment/:jobId/edit"
              element={
                <ProtectedRoute roles={["super_admin", "admin", "designer", "estimator"]}>
                  <EditAssignment />
                </ProtectedRoute>
              }
            />

            {/* Schedule Routes */}
            <Route
              path="/:companySlug/schedule"
              element={
                <ProtectedRoute roles={["super_admin", "admin", "designer", "estimator"]}>
                  <ScheduleHub />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:companySlug/schedule/editor"
              element={
                <ProtectedRoute roles={["super_admin", "admin", "designer", "estimator"]}>
                  <EditorSchedule />
                </ProtectedRoute>
              }
            />

            <Route
              path="/:companySlug/schedule/production"
              element={
                <ProtectedRoute roles={["super_admin", "admin", "designer", "estimator"]}>
                  <ProductionSchedule />
                </ProtectedRoute>
              }
            />

            <Route
              path="/:companySlug/schedule/delivery"
              element={
                <ProtectedRoute roles={["super_admin", "admin", "designer", "estimator"]}>
                  <DeliveryQuoteSchedule />
                </ProtectedRoute>
              }
            />

            <Route
              path="/:companySlug/schedule/:quoteType"
              element={
                <ProtectedRoute roles={["super_admin", "admin", "designer", "estimator"]}>
                  <QuoteSchedule />
                </ProtectedRoute>
              }
            />

            <Route path="/track" element={<DeliveryTracker />} />
            <Route path="/track/:trackingId" element={<DeliveryTracker />} />

            <Route path="/" element={<RootRedirect />} />
            <Route path="*" element={<NotFound />} />


          </Routes>
        </Layout>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;